import React, { useMemo, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Flexbox from 'flexbox-react';
import Pagination from 'react-js-pagination';
import {
  Card,
  InputGroup,
  ProgressBar,
  Tag,
  Intent,
  Tooltip,
  Icon,
  Button
} from '@blueprintjs/core';
import ScrollableDropdown from '../shared/ScrollableDropdown';
import { useHorizontalScrollNav } from '../../hooks/useHorizontalScrollNav';
import '../../css/performanceAndScroll.css';
import './CheckInsReport.scss';
import { REVIEW_PERIOD_LABELS } from '../../static/Constant.js';
import CurrentUserStore from '../../stores/CurrentUserStore';
import PerformanceTableInfoCard from '../goal_performance/PerformanceTableInfoCard';

const defaultJobProfile = { id: 'all', name: 'All Job Profiles' };

const CheckInsReport = () => {
  const [query, setQuery] = useState('');
  const [reportRows, setReportRows] = useState([]);
  const [reportCards, setReportCards] = useState([]);
  const [jobProfileOptions, setJobProfileOptions] = useState([defaultJobProfile]);
  const [cycleOptions, setCycleOptions] = useState([]);
  const [jobProfile, setJobProfile] = useState(defaultJobProfile);
  const [cycle, setCycle] = useState({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [hasLoadedRows, setHasLoadedRows] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const fetchDropdownData = async () => {
      const apiEnv = Cookies.get('apiEnv');
      if (!apiEnv) return;

      try {
        const [jpRes, gcRes] = await Promise.all([
          axios.get(`${apiEnv}/job_profiles`),
          axios.get(`${apiEnv}/goal_settings/goal_cycle_list`)
        ]);

        if (jpRes.data && Array.isArray(jpRes.data)) {
          setJobProfileOptions([defaultJobProfile, ...jpRes.data]);
        } else if (jpRes.data && jpRes.data.job_profiles) {
          setJobProfileOptions([defaultJobProfile, ...jpRes.data.job_profiles]);
        }

        let goalCycleOptions = [];
        if (gcRes?.data && Array.isArray(gcRes?.data)) {
          goalCycleOptions = gcRes?.data?.map((item) => ({
            id: item?.id || item?.goal_setting_id,
            title: item?.title || item?.goal_setting_title || item?.name || item?.goal_setting_title || 'Unknown Cycle',
            raw: item,
          }));
        } else if (gcRes?.data && gcRes?.data?.goal_settings) {
          goalCycleOptions = gcRes?.data?.goal_settings?.map((item) => ({
            id: item?.id || item?.goal_setting_id,
            title: item?.title || item?.goal_setting_title || item?.name || item?.goal_setting_title || 'Unknown Cycle',
            raw: item,
          }));
        }

        if (goalCycleOptions.length > 0) {
          setCycleOptions(goalCycleOptions);
          setCycle(goalCycleOptions[0]);
        }
      } catch (error) {
        console.error('Error fetching report dropdown data:', error);
      }
    };

    fetchDropdownData();
  }, []);

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchReportRows = async () => {
      const apiEnv = Cookies.get('apiEnv');
      if (!apiEnv || !cycle.id || cycle.id === 'all') {
        setReportRows([]);
        setTotalUsers(0);
        setTotalPages(0);
        setHasLoadedRows(true);
        return;
      }

      setHasLoadedRows(false);
      setIsLoadingUsers(true);
      try {
        const params = {
          goal_setting_id: cycle.id,
          page: currentPage,
          per_page: perPage,
        };

        if (query && query.trim().length > 0) params.search = query.trim();

        const response = await axios.get(`${apiEnv}/review_workflows/goal_report_rating`, {
          params,
        });

        const responseData = response.data || {};
        const rows = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.data)
            ? responseData?.data
            : [];
        const meta = responseData?.meta || {};

        const totalCount = Number.isFinite(meta.total_count)
          ? meta.total_count
          : Number.isFinite(responseData?.total_count)
            ? responseData?.total_count
            : rows.length;
        const perPageValue = Number.isFinite(meta.per_page)
          ? meta.per_page
          : Number.isFinite(responseData?.per_page)
            ? responseData?.per_page
            : perPage;
        const currentPageValue = Number.isFinite(meta.page)
          ? meta.page
          : currentPage;
        const totalPagesValue = Number.isFinite(meta.total_pages)
          ? meta.total_pages
          : perPageValue > 0
            ? Math.max(1, Math.ceil(totalCount / perPageValue))
            : 1;

        setReportRows(rows);
        setTotalUsers(totalCount);
        setPerPage(perPageValue);
        setCurrentPage(currentPageValue);
        setTotalPages(totalPagesValue);
        setHasLoadedRows(true);
      } catch (error) {
        console.error('Error fetching check-ins report rows:', error);
        setReportRows([]);
        setTotalUsers(0);
        setTotalPages(0);
        setHasLoadedRows(true);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    // debounce server-side search to avoid spamming API
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchReportRows();
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [cycle.id, query, currentPage, perPage]);

  const handleSearchChange = (event) => {
    const val = event.target.value;
    setQuery(val);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePerPageChange = (event) => {
    const pageSize = parseInt(event.target.value, 10) || 10;
    setPerPage(pageSize);
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchReportCards = async () => {
      const apiEnv = Cookies.get('apiEnv');
      if (!apiEnv || !cycle.id || cycle.id === 'all') {
        setReportCards([]);
        return;
      }

      try {
        const response = await axios.get(`${apiEnv}/insights_report/goal_progress_report`, {
          params: { goal_setting_id: cycle.id }
        });

        const data = response?.data;
        if (data?.checkins && data?.checkins.length > 0) {
          const mappedCards = data?.checkins?.map((c, index) => ({
            id: c?.checkin_id || `checkin-${index}`,
            title: c?.checkin_title,
            completed: c?.users_with_progress,
            notCompleted: c?.users_without_progress
          }));
          setReportCards(mappedCards);
        } else if (data?.goal_setting_title) {
          setReportCards([{
            id: cycle?.id,
            title: data?.goal_setting_title,
            completed: data?.users_with_progress || 0,
            notCompleted: data?.users_without_progress || 0
          }]);
        } else {
          setReportCards([]);
        }
      } catch (error) {
        console.error('Error fetching report cards:', error);
        setReportCards([]);
      }
    };

    fetchReportCards();
  }, [cycle.id]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      const apiEnv = Cookies.get("apiEnv");
      if (!apiEnv) return;

      try {
        const [jpRes, gcRes] = await Promise.all([
          axios.get(`${apiEnv}/job_profiles`),
          axios.get(`${apiEnv}/goal_settings/goal_cycle_list`)
        ]);

        if (jpRes.data && Array.isArray(jpRes.data)) {
          setJobProfileOptions([defaultJobProfile, ...jpRes.data]);
        } else if (jpRes.data && jpRes.data.job_profiles) {
          setJobProfileOptions([defaultJobProfile, ...jpRes.data.job_profiles]);
        }

        if (gcRes.data && Array.isArray(gcRes.data)) {
          setCycleOptions([defaultCycle, ...gcRes.data]);
        }
      } catch (error) {
        console.error("Error fetching report dropdown data:", error);
      }
    };

    fetchDropdownData();
  }, []);

  const {
    scrollRef: reportCardsScrollRef,
    canScrollLeft: canScrollReportCardsLeft,
    canScrollRight: canScrollReportCardsRight,
    showArrows: showReportCardsArrows,
    scrollByDir: scrollReportCardsByDir,
  } = useHorizontalScrollNav({
    arrowsVisibility: 'when-overflow',
    layoutDependency: reportCards.length,
    scrollIntoViewEnabled: false,
    minScrollChunk: 200,
    scrollViewportRatio: 0.65,
  });

  const filteredRows = useMemo(() => {
    return reportRows.filter((row) => {
      const matchesJobProfile =
        jobProfile.id === 'all' ||
        (row.employee?.role && row.employee.role === jobProfile.name);
      const matchesCycle =
        cycle.id === 'all' ||
        row.goal_setting_id === cycle.id ||
        row.goal_setting_title === cycle.title;
      return matchesJobProfile && matchesCycle;
    });
  }, [jobProfile, cycle, reportRows]);

  const showTableLoader = isLoadingUsers || !hasLoadedRows;

  const tableColumnLayout = useMemo(() => {
    const hasRows = filteredRows.length > 0;
    const maxCheckins = filteredRows.reduce(
      (max, row) => Math.max(max, row.checkins?.length || 0),
      0
    );

    if (!hasRows) {
      return {
        mode: 'empty',
        goal: '36%',
        employee: '30%',
        checkins: '34%',
      };
    }

    if (maxCheckins === 0) {
      return {
        mode: 'checkins-none',
        goal: '32%',
        employee: '26%',
        checkins: '42%',
      };
    }

    if (maxCheckins <= 3) {
      return {
        mode: 'checkins-few',
        goal: '24%',
        employee: '18%',
        checkins: '58%',
      };
    }

    if (maxCheckins <= 6) {
      return {
        mode: 'checkins-medium',
        goal: '20%',
        employee: '15%',
        checkins: '65%',
      };
    }

    return {
      mode: 'checkins-many',
      goal: '16%',
      employee: '12%',
      checkins: '72%',
    };
  }, [filteredRows]);

  return (
    <div className="checkin-report">
      <div className="checkins-report-header">
        <div className="checkins-report-header-text">
          <h2>Check-ins</h2>
          <p>
            Track check-in progress and pacing against target dates across your team's smart goals.
          </p>
        </div>

        <div className="checkins-report-header-controls">
          <InputGroup
            className="checkins-report-search"
            leftIcon="search"
            placeholder="Search by employee..."
            value={query}
            onChange={handleSearchChange}
            large
          />
          <div className="checkins-report-cycle-dropdown">
            <ScrollableDropdown
              width={240}
              label={cycle.title || 'Select Goal Cycle'}
              items={cycleOptions}
              getKey={(item) => item.id}
              getLabel={(item) => item.title}
              onSelect={(item) => {
                setCycle(item);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {reportCards.length > 0 && (
        <div className="checkins-cards-scroll-row">
          <div className="checkins-cards-scroll-viewport">
            {showReportCardsArrows && (
              <Button
                type="button"
                className="checkins-cards-scroll-arrow checkins-cards-scroll-arrow--left"
                aria-label="Scroll check-ins left"
                icon="chevron-left"
                minimal
                small
                disabled={!canScrollReportCardsLeft}
                onClick={() => scrollReportCardsByDir(-1)}
              />
            )}
            <div
              ref={reportCardsScrollRef}
              className="checkins-cards-scroll-track u-hide-scrollbar-x"
            >
              {reportCards.map((card) => (
                <div key={card.id} style={{ flex: '0 0 auto' }}>
                  <PerformanceTableInfoCard card={card} fullTitle />
                </div>
              ))}
            </div>
            {showReportCardsArrows && (
              <Button
                type="button"
                className="checkins-cards-scroll-arrow checkins-cards-scroll-arrow--right"
                aria-label="Scroll check-ins right"
                icon="chevron-right"
                minimal
                small
                disabled={!canScrollReportCardsRight}
                onClick={() => scrollReportCardsByDir(1)}
              />
            )}
          </div>
        </div>
      )}

      <Card
        className={`checkins-report-table-card checkins-report-table-card--${tableColumnLayout.mode}`}
        elevation={0}
        style={{ padding: 0, borderRadius: '6px', overflow: 'hidden' }}
      >
        <table
          className={`bp3-html-table bp3-interactive report-table report-table--${tableColumnLayout.mode}`}
        >
          <thead style={{ background: '#F8FAFC' }}>
            <tr>
              <th
                className="goal-column"
                style={{ width: tableColumnLayout.goal }}
              >
                Goal
              </th>

              <th
                className="employee-column"
                style={{ width: tableColumnLayout.employee }}
              >
                Employee
              </th>

              <th
                className="checkins-column"
                style={{ width: tableColumnLayout.checkins }}
              >
                Check-ins
              </th>
            </tr>
          </thead>
          <tbody style={{ background: '#fff' }}>
            {showTableLoader ? (
              <tr>
                <td colSpan="3" style={{ textAlign: 'center', padding: '30px 24px' }}>
                  <img
                    style={{ height: '35px', width: '35px' }}
                    src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif"
                    alt="Loading..."
                  />
                </td>
              </tr>
            ) : null}
            {!showTableLoader && filteredRows.map((row) => {
              const goalName = row.goal_name || row.goal || 'Untitled Goal';
              const frequencyLabel = REVIEW_PERIOD_LABELS[row.goal_setting_period] || row.frequency || '';
              const dueDate = row.goal_setting_end_date
                ? new Date(row.goal_setting_end_date).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
                : row.dueDate || '';
              const employeeName = row.employee?.name || 'Unknown';

              return (
                <tr key={row.goal_id || row.id || goalName}>
                  <td className="goal-column-cell">
                    <Tooltip portalContainer={document.body} content={goalName} disabled={goalName.length <= 30} position="top" wrapperTagName="div" targetTagName="div">
                      <div
                        style={{
                          fontWeight: 400,
                          fontSize: '14px',
                          marginBottom: '8px',
                          color: '#4d4d4d',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {goalName}
                      </div>
                    </Tooltip>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {frequencyLabel && <Tag minimal style={{ color: '#4d4d4d' }}>{frequencyLabel}</Tag>}
                      {dueDate && (
                        <span style={{ color: '#4d4d4d', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Icon icon="calendar" size={12} style={{ color: '#6B7280' }} />
                          {`Due ${dueDate}`}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="employee-column-cell">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: '#6366F1',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '14px',
                        }}
                      >
                        {employeeName
                          .split(' ')
                          .map((part) => part.charAt(0))
                          .slice(0, 2)
                          .join('')}
                      </div>
                      <div>
                        <div style={{ fontWeight: 400, color: '#4d4d4d', fontSize: '14px' }}>{employeeName}</div>
                      </div>
                    </div>
                  </td>
                  <td className="checkins-column-cell">
                    <div className="checkins-scroll u-hide-scrollbar-x">
                      <div className="checkin-row-cards-track">
                        {(row.checkins && row.checkins.length > 0) ? (
                          row.checkins.map((checkin, index) => {
                            const progress = typeof checkin.progress === 'number' ? checkin.progress : null;
                            const percent = progress !== null ? progress : 0;
                            const intent = progress === null
                              ? Intent.NONE
                              : progress >= 80
                                ? Intent.SUCCESS
                                : progress >= 50
                                  ? Intent.PRIMARY
                                  : Intent.WARNING;
                            const period = checkin.checkin_title || checkin.period || 'Check-in';
                            const isFuture = checkin.current_state === 2;

                            const cardNode = (
                              <Card
                                interactive={!isFuture}
                                className="checkin-row-card"
                                onClick={() => {
                                  if (isFuture) return;

                                  const userId = row?.employee?.user_id || row?.employee?.id || row?.user_id || row?.employee_id || row?.id;
                                  const goalSettingId = row?.goal_setting_id || cycle.id;
                                  const empName = row?.employee?.name || 'Unknown';
                                  const checkinId = checkin.checkin_id || checkin.id || null;

                                  const payload = {
                                    u: userId,
                                    g: goalSettingId,
                                    m: null,
                                    n: empName
                                  };
                                  if (checkinId) {
                                    payload.c = checkinId;
                                  }

                                  const r = btoa(JSON.stringify(payload));
                                  const currentUserId = CurrentUserStore.currentUser.id;

                                  if (String(userId) === String(currentUserId)) {
                                    window.location.href = `/performance/myassessment?r=${r}`;
                                  } else {
                                    window.location.href = `/performance/team/review?r=${r}`;
                                  }
                                }}
                                elevation={0}
                                style={{
                                  opacity: isFuture ? 0.5 : 1,
                                  cursor: isFuture ? 'not-allowed' : 'pointer',
                                }}
                              >
                                <div className="checkin-row-card-body">
                                  <div className="checkin-row-card-header">
                                    <div className="checkin-row-card-title-tooltip">
                                      <Tooltip portalContainer={document.body}
                                        content={isFuture ? (
                                          <div style={{ textAlign: 'left', padding: '4px 2px', maxWidth: '240px' }}>
                                            <div style={{ fontWeight: 600, marginBottom: '6px' }}> Check-in Unavailable</div>
                                            <div style={{ fontSize: '13px', opacity: 0.9, lineHeight: '1.4' }}>
                                                  Available when the scheduled period begins.
                                            </div>
                                          </div>
                                        ) : period} 
                                        disabled={!isFuture && period.length <= 25} 
                                        position="top" 
                                        hoverOpenDelay={100} 
                                        boundary="viewport" 
                                        popoverClassName="custom-tooltip-width"
                                      >
                                        <span>{period}</span>
                                      </Tooltip>
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 'auto', paddingTop: '8px' }}>
                                    <ProgressBar
                                      className="checkin-row-card-progress"
                                      value={Math.max(0, Math.min(1, percent / 100))}
                                      stripes={false}
                                      intent={intent}
                                    />
                                    <Tag minimal intent={intent} className="checkin-row-card-tag">
                                      {progress !== null ? `${progress}%` : '0%'}
                                    </Tag>
                                  </div>
                                </div>
                              </Card>
                            );

                            return (
                              <React.Fragment key={`${row.goal_id || row.id}-${period}-${index}`}>
                                {cardNode}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0' }}>
                            <div style={{
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              border: '2px dashed #9CA3AF',
                            }}></div>
                            <span style={{ fontSize: '14px', color: '#6B7280' }}>No check-ins yet</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )
            })}
            {!showTableLoader && filteredRows.length === 0 && (
              <tr className="no-data-row" style={{ background: '#fff' }}>
                <td colSpan="3" style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <div style={{ marginBottom: '8px', fontSize: '16px' }}>No data found.</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {!(isLoadingUsers || filteredRows.length === 0 || totalUsers === 0) && (
          <Flexbox
            justifyContent="space-between"
            marginTop="16px"
            alignItems="center"
            flexDirection="row"
            style={{ padding: '16px' }}
          >
            <Flexbox flexDirection="column" alignItems="center">
              <Pagination
                activePage={currentPage}
                itemsCountPerPage={perPage}
                totalItemsCount={totalUsers}
                pageRangeDisplayed={Math.min(5, totalPages || 5)}
                onChange={handlePageChange}
                disabledClass="bp3-disabled"
                innerClass="bp3-button-group bp3-minimal bp3-pagination"
                itemClass="bp3-button"
                activeClass="bp3-active bp3-pagination-active"
                linkClass="no-underline"
                prevPageText={<span className="bp3-icon bp3-icon-chevron-left" />}
                nextPageText={<span className="bp3-icon bp3-icon-chevron-right" />}
                firstPageText={<span className="bp3-icon bp3-icon-double-chevron-left" />}
                lastPageText={<span className="bp3-icon bp3-icon-double-chevron-right" />}
              />
            </Flexbox>

            <Flexbox alignItems="center">
              <span
                className="table-header-text"
                style={{ marginRight: 8 }}
              >
                Show
              </span>

              <select
                value={perPage}
                onChange={handlePerPageChange}
                className="bp3-input"
                style={{ width: 80, marginRight: 8 }}
              >
                {[10, 25, 40, 100].map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>

              <span className="table-header-text">
                per page
              </span>
            </Flexbox>
          </Flexbox>
        )}
      </Card>
    </div>
  );
};

export default CheckInsReport;